import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft, ScrollText, ImagePlus, Music4, Clapperboard, Film,
  Wand2, Zap, X, Server, Landmark, Binary,
} from "lucide-react";
import MistMusic from "@/components/mist/MistMusic";
import MistStitcher from "@/components/mist/MistStitcher";
import MistCreate from "@/components/mist/MistCreate";
import MistDirectorMode from "@/components/mist/MistDirectorMode";
import DirectorStudio from "@/components/mist/DirectorStudio";
import SequentialDirector from "@/components/mist/SequentialDirector";
import ContinueShot from "@/components/mist/ContinueShot";
import MistPoem from "@/components/mist/MistPoem";
import OutroVault from "@/components/mist/OutroVault";
import FounderCipherStudio from "@/components/mist/FounderCipherStudio";
import CommunityVault from "@/components/mist/CommunityVault";
import VideoEditorWorkspace from "@/components/editor/VideoEditorWorkspace";
import { Globe } from "lucide-react";
import { useSession } from "@/context/SessionContext";

const PREMIUM = [];

const NODES = [
  { key: "directormode", title: "Director Studio", sub: "Timeline · Music Viz · Reels", Icon: Clapperboard, color: "#FFE08A" },
  { key: "create", title: "Mist Create", sub: "Generate · Animate · Effects", Icon: ImagePlus, color: "#A4DCFA" },
  { key: "poem", title: "Mist Poem", sub: "AI creative writing", Icon: ScrollText, color: "#FFD700" },
  { key: "music", title: "Mist Music", sub: "3-tab track engine", Icon: Music4, color: "#9EFFB8" },
  { key: "editor", title: "Video Editor", sub: "Trim · Stylize · Post · $0 local", Icon: Film, color: "#F2C94C" },
  { key: "cipher", title: "Cipher Studio", sub: "Founder cryptic promo art", Icon: Binary, color: "#39FF7A", founderOnly: true },
  { key: "community", title: "Community Vault", sub: "Shared by everyone · reuse", Icon: Globe, color: "#FFE08A" },
  { key: "sequential", title: "Sequential Director", sub: "6 scenes · continuity bridge", Icon: Film, color: "#C9B6FF", deprecated: true },
  { key: "continue", title: "Continue Shot", sub: "Chain shots · Director", Icon: Film, color: "#FFB4A2", deprecated: true },
  { key: "stitcher", title: "Mist Stitcher", sub: "Timeline → Reel", Icon: Film, color: "#E8D3FF", deprecated: true },
  { key: "outro", title: "Outro Vault", sub: "Design & save custom outros", Icon: Clapperboard, color: "#FFD028", deprecated: true },
];

const TEMPLATES = [
  { label: "Cinematic hero image", node: "create", prompt: "A cinematic hero shot, dramatic rim lighting, ultra-detailed, 9:16" },
  { label: "Motivational poem", node: "poem", prompt: "A short motivational poem about relentless builders chasing a dream" },
  { label: "Anthem lyrics", node: "music", prompt: "" },
  { label: "Product promo reel", node: "directormode", prompt: "" },
];

/* --------------------------------- POEM --------------------------------- */
/* MistPoem now imported from ./MistPoem.jsx (2-tab: Create Poem · Add to Video) */


/* Desktop (lg:+) detector — drives the docked studio right-pane. Below lg the
   studios keep opening full-screen inside the phone chassis (mobile behaviour). */
function useIsDesktop() {
  const [d, setD] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setD(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return d;
}

/* Docked desktop studio pane — portalled to <body> so it escapes the phone
   chassis CSS transform. Slides in from the right edge, sticky X header,
   isolated vertical scroll so the fixed hub grid never moves. */
function StudioPane({ title, onClose, children }) {
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEnter(true));
    return () => cancelAnimationFrame(r);
  }, []);
  const handleClose = () => { setEnter(false); setTimeout(onClose, 280); };
  return (
    <div
      className="hidden lg:flex fixed top-0 bottom-0 right-0 z-[60] flex-col bg-black border-l-4 border-[#2a2a2a] shadow-[-10px_0_28px_rgba(0,0,0,0.55)] transition-transform duration-300 ease-out"
      style={{ left: "508px", transform: enter ? "translateX(0)" : "translateX(101%)" }}
      data-testid="studio-right-pane"
    >
      <div className="shrink-0 sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-[#141414] border-b-4 border-[#2a2a2a]">
        <div className="flex-1 leading-tight min-w-0">
          <h2 className="font-serif text-xl text-white uppercase truncate">{title}</h2>
          <p className="text-[9px] text-gray-400 font-mono uppercase tracking-[0.24em] mt-0.5">Studio Workspace</p>
        </div>
        <button onClick={handleClose} data-testid="studio-pane-close" aria-label="Close studio"
          className="w-10 h-10 rounded-full bg-[#141414] border-2 border-[#2a2a2a] grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <X size={18} strokeWidth={2.4} className="text-white" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative" data-testid="studio-pane-body">
        {children}
      </div>
    </div>
  );
}

/* ---------------------------- SUITE ROUTER ------------------------------ */
export default function MistSuite({ onBack }) {
  const [node, setNode] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("studio");
    return NODES.some((n) => n.key === s) ? s : null;
  });
  const [prefill, setPrefill] = useState("");
  const [outroTemplate, setOutroTemplate] = useState(null);   // deep-link outro id to edit
  const [macros, setMacros] = useState(false);
  const { user } = useSession();
  const isFounder = !!user && (user.role === "founder" || user.handle === "johnnywalker" || user.email === "djohnm1972@gmail.com");
  // Civilization purged — premium studios are governed purely by the
  // subscription/credit entitlement at the backend charge point. Always open in the UI.
  const hasFeature = () => true;

  const writeStudioUrl = (key, push) => {
    const p = new URLSearchParams(window.location.search);
    if (key) p.set("studio", key);
    else { p.delete("studio"); p.delete("view"); }
    const qs = p.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
    if (push) window.history.pushState({ studio: key || null }, "", url);
    else window.history.replaceState(window.history.state, "", url);
  };

  const goNode = (key, pf = "") => { setPrefill(pf); setNode(key); writeStudioUrl(key, true); };
  const active = NODES.find((n) => n.key === node);
  const isDesktop = useIsDesktop();
  const showGrid = !node || isDesktop;   // desktop: the 1/3 chassis stays the navigator; studio opens in the 2/3 pane

  const back = () => {
    // View-aware fallback: if a sub-studio view is open (e.g. Voice Lab
    // ?view=lab), first collapse to the studio's own landing roster; only the
    // next press exits the studio node → Mist Creations grid → dashboard.
    const p = new URLSearchParams(window.location.search);
    if (node && p.get("view")) {
      p.delete("view");
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    if (node) { setNode(null); setOutroTemplate(null); writeStudioUrl(null, true); return; }
    onBack?.();
  };

  // The active studio, mounted ONCE — into the chassis body on mobile or the
  // docked right-pane on desktop (never both, so studio state is preserved).
  const studioEl = (
    node === "directormode" ? <DirectorStudio onExit={back} initialTab={new URLSearchParams(window.location.search).get("dstab") || "director"} /> :
    node === "sequential" ? <SequentialDirector onExit={back} /> :
    node === "continue" ? <ContinueShot /> :
    node === "create" ? <MistCreate /> :
    node === "community" ? <CommunityVault onUsed={(track) => goNode(track === "music" ? "music" : track === "video" ? "stitcher" : "create")} /> :
    node === "poem" ? <MistPoem initialPrompt={prefill} /> :
    node === "music" ? <MistMusic /> :
    node === "stitcher" ? <MistStitcher /> :
    node === "outro" ? <OutroVault onBack={back} templateId={outroTemplate} /> :
    node === "editor" ? <VideoEditorWorkspace /> :
    node === "cipher" ? (isFounder ? <FounderCipherStudio onSendToDirector={() => goNode("directormode")} /> : null) :
    null
  );

  // Re-hydrate the open studio on pull-to-refresh / browser back-forward.
  useEffect(() => {
    const onPop = () => {
      const s = new URLSearchParams(window.location.search).get("studio");
      setNode(NODES.some((n) => n.key === s) ? s : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div className="absolute inset-0 z-[45] flex flex-col bg-black" data-testid="mist-suite">
      {/* Macro quick-inject sheet */}
      {macros && (
        <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="macro-sheet">
          <div className="w-full bg-black border-t-4 border-[#2a2a2a] rounded-t-3xl p-5">
            <div className="flex items-center gap-2 pb-3 border-b-2 border-[#2a2a2a]/10 mb-3">
              <Zap size={16} className="text-gold" />
              <h3 className="font-serif text-xl text-white flex-1">Quick Macros</h3>
              <button onClick={() => setMacros(false)} data-testid="macro-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-[#2a2a2a] bg-[#141414] shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} /></button>
            </div>
            <div className="space-y-2">
              {TEMPLATES.map((t) => (
                <button key={t.label} onClick={() => { setMacros(false); goNode(t.node, t.prompt); }} data-testid={`macro-${t.node}`}
                  className="w-full flex items-center gap-2 bg-[#141414] border-2 border-[#2a2a2a] rounded-xl px-3 py-2.5 shadow-[2px_2px_0px_#1A1A1A] text-left active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                  <Wand2 size={14} className="text-gold shrink-0" />
                  <span className="font-mono text-[11px] font-bold text-white">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header — single-thumb back arrow */}
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-4 border-[#2a2a2a] bg-[#141414]">
        <button onClick={back} data-testid="mist-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-[#141414] border-2 border-[#2a2a2a] grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} strokeWidth={2.4} />
        </button>
        <div className="leading-tight flex-1">
          <h1 className="font-serif text-2xl text-white uppercase">{showGrid ? "Mist Creations" : active.title}</h1>
          <p className="text-[9px] text-gray-400 font-mono uppercase tracking-[0.24em] mt-0.5">Mist Suite</p>
        </div>
        {showGrid && (
          <button onClick={() => setMacros(true)} data-testid="macro-btn" className="flex items-center gap-1 bg-gold border-2 border-[#2a2a2a] rounded-full pl-2 pr-3 py-2 shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Zap size={15} strokeWidth={2.6} className="text-black" /><span className="font-mono text-[9px] font-black uppercase tracking-widest text-black">Macros</span>
          </button>
        )}
      </header>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {showGrid && (
          <div className="h-full overflow-y-auto no-scrollbar px-4 py-4">
            <div className="grid grid-cols-2 gap-3" data-testid="mist-node-grid">
              {NODES.filter((n) => !n.deprecated && (!n.founderOnly || isFounder)).map((n) => (
                <button key={n.key} onClick={() => { if (n.key === "outro") setOutroTemplate(null); goNode(n.key); }} data-testid={`mist-node-${n.key}`}
                  className={`relative aspect-square rounded-2xl border-4 flex flex-col items-start justify-between p-3.5 text-left shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform duration-150 ${isDesktop && node === n.key ? "border-gold ring-2 ring-gold" : "border-[#2a2a2a]"}`}
                  style={{ backgroundColor: n.color }}>
                  <span className="w-11 h-11 rounded-xl border-2 border-black/25 bg-black/70 grid place-items-center"><n.Icon size={22} strokeWidth={2.2} className="text-white" /></span>
                  <div>
                    <p className="font-serif text-lg leading-tight text-[#1A1A1A]">{n.title}</p>
                    <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-[#1A1A1A]/70 mt-0.5">{n.sub}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Premium unlocks — gated behind Misteck Civilization milestones */}
            {PREMIUM.length > 0 && (
            <div className="mt-5">
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.24em] text-gold mb-2">Premium Unlocks</p>
              <div className="grid grid-cols-2 gap-3" data-testid="mist-premium-grid">
                {PREMIUM.map((p) => {
                  return (
                    <button key={p.feature} data-testid={`mist-premium-${p.feature}`}
                      onClick={() => goNode(p.node)}
                      className="relative aspect-square rounded-2xl border-4 border-[#2a2a2a] flex flex-col items-start justify-between p-3.5 text-left shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform"
                      style={{ backgroundColor: p.color }}>
                      <span className="w-11 h-11 rounded-xl border-2 border-[#2a2a2a] bg-[#141414]/60 grid place-items-center"><p.Icon size={22} strokeWidth={2.2} className="text-white" /></span>
                      <div>
                        <p className="font-serif text-lg leading-tight text-white">{p.title}</p>
                        <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-white/70 mt-0.5">{p.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        )}
        {/* Mobile (<lg): studio opens full-screen inside the 1/3 chassis. */}
        {node && !isDesktop && studioEl}
      </div>

      {/* Desktop (lg:+): studio fills the 2/3 Dynamic Workspace Pane, replacing
          the Control Deck; the 1/3 chassis stays as the persistent navigator. */}
      {isDesktop && node && createPortal(
        <StudioPane title={active?.title || "Studio"} onClose={back}>{studioEl}</StudioPane>,
        document.body
      )}
    </div>
  );
}
